import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api";
import type { DisputeOutcome, ReturnPageFilter, ReturnResolveInput, ReturnStatus } from "@/api";

// Возвраты (fd-order-19..22). Список и сводка читаются, решения/отметки
// меняют стадию заявки — после каждой мутации инвалидируем и список, и сводку,
// чтобы счётчики стадий не разошлись с таблицей.
export const returnKeys = {
	all: ["returns"] as const,
	list: (status?: ReturnStatus) => ["returns", "list", status ?? "all"] as const,
	page: (filter: ReturnPageFilter) => ["returns", "page", filter] as const,
	summary: ["returns", "summary"] as const,
};

export function useReturns(status?: ReturnStatus) {
	return useQuery({
		queryKey: returnKeys.list(status),
		queryFn: () => api.returns.list(status),
	});
}

/** Страница заявок с сервера; прежняя видна, пока грузится следующая. */
export function useReturnPage(filter: ReturnPageFilter) {
	return useQuery({
		queryKey: returnKeys.page(filter),
		queryFn: () => api.returns.page(filter),
		placeholderData: keepPreviousData,
	});
}

export function useReturnsSummary() {
	return useQuery({
		queryKey: returnKeys.summary,
		queryFn: () => api.returns.summary(),
	});
}

function useReturnMutation<V>(mutationFn: (vars: V) => Promise<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSettled: () => queryClient.invalidateQueries({ queryKey: returnKeys.all }),
	});
}

export function useResolveReturn() {
	return useReturnMutation<{ id: string; input: ReturnResolveInput }>(({ id, input }) =>
		api.returns.resolve(id, input),
	);
}

export function useReceiveReturn() {
	return useReturnMutation<string>((id) => api.returns.received(id));
}

export function useRefundReturn() {
	return useReturnMutation<string>((id) => api.returns.refunded(id));
}

export function useReturnDetail(id: string | null) {
	return useQuery({
		queryKey: ["returns", "detail", id ?? ""] as const,
		queryFn: () => api.returns.get(id ?? ""),
		enabled: !!id,
	});
}

export function useResolveDispute() {
	return useReturnMutation<{ id: string; outcome: DisputeOutcome; note?: string }>(({ id, outcome, note }) =>
		api.returns.resolveDispute(id, { outcome, note }),
	);
}
